import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { type AnchorHTMLAttributes, type ElementType, forwardRef } from 'react';

import { Text } from '../Typography';
import { linkBaseStyles, linkUnderlineStyles } from './Link.styles';
import { type LinkUnderline } from './Link.types';

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  /**
   * Underline behavior
   */
  underline?: LinkUnderline;
  /**
   * Element type to render as (e.g. Next.js Link)
   */
  as?: ElementType;
  /**
   * Additional CSS class names
   */
  className?: string;
};

type StyledLinkProps = {
  linkUnderline: LinkUnderline;
};

// Compose with isPropValid so Emotion still consumes reserved props like `as`
// (custom shouldForwardProp replaces the default filter entirely).
const StyledLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'linkUnderline' && isPropValid(prop),
})<StyledLinkProps>`
  ${linkBaseStyles}
  ${({ linkUnderline }) => linkUnderlineStyles[linkUnderline]}
`;

const wrapChild = (children: LinkProps['children']) => {
  if (typeof children === 'string' || typeof children === 'number') {
    return <Text as="span">{children}</Text>;
  }

  return children;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ underline = 'hover', as, className, children, ...rest }, ref) => {
    return (
      <StyledLink
        as={as}
        ref={ref}
        linkUnderline={underline}
        className={className}
        {...rest}
      >
        {wrapChild(children)}
      </StyledLink>
    );
  }
);

Link.displayName = 'Link';

export default Link;
